import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { backend } from "../../../path";
import DashboardSidebarMain from "../dashsidebar";
import HeaderDashMain from "../../layout/header/dash header";

function GoogleMerchantMain() {
  const [authenticated] = useState(
    JSON.parse(localStorage.getItem("userdata"))
  );
  
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState({
    connection: { status: "disconnected", message: "Loading connection status..." },
    stats: {
      total_products: 0,
      synchronized_products: 0,
      failed_products: 0,
      pending_products: 0,
      last_sync_time: null,
    },
    diagnostics: [],
    history: { data: [], current_page: 1, last_page: 1 },
  });

  const [settings, setSettings] = useState({
    merchant_id: "",
    target_country: "IN",
    content_language: "en",
    currency: "INR",
    default_category: "Food, Beverages & Tobacco",
    shipping_cost: 0,
    tax_rate: 0,
  });

  const [historyPage, setHistoryPage] = useState(1);

  // Fetch connection, stats, history & diagnostics
  const fetchStatusAndStats = () => {
    setLoading(true);
    fetch(`${backend}google-merchant/status?page=${historyPage}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${authenticated?.access_token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        return res.json();
      })
      .then((resData) => {
        setData(resData);
      })
      .catch((err) => {
        console.error("Failed to load status/stats:", err);
        toast.error("Failed to fetch connection status and statistics.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch settings
  const fetchSettings = () => {
    fetch(`${backend}google-merchant/settings`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${authenticated?.access_token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        return res.json();
      })
      .then((resData) => {
        if (resData.settings) {
          setSettings(resData.settings);
        }
      })
      .catch((err) => {
        console.error("Failed to load settings:", err);
      });
  };

  useEffect(() => {
    fetchStatusAndStats();
    fetchSettings();
  }, [historyPage]);

  // Handle settings update
  const handleSaveSettings = (e) => {
    e.preventDefault();
    setLoading(true);
    fetch(`${backend}google-merchant/settings`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authenticated?.access_token}`,
      },
      body: JSON.stringify(settings),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        return res.json();
      })
      .then(() => {
        toast.success("Google Merchant settings saved successfully!");
        fetchStatusAndStats(); // Refresh connection status since settings changed
      })
      .catch((err) => {
        console.error("Failed to save settings:", err);
        toast.error("Failed to save settings. Please verify details.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Trigger manual sync
  const handleManualSync = (retryFailed = false) => {
    setSyncing(true);
    fetch(`${backend}google-merchant/sync`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authenticated?.access_token}`,
      },
      body: JSON.stringify({ retry_failed: retryFailed }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        return res.json();
      })
      .then((resData) => {
        toast.success(resData.message || "Manual sync enqueued successfully!");
        // Refresh stats after a short delay
        setTimeout(fetchStatusAndStats, 2000);
      })
      .catch((err) => {
        console.error("Manual sync failed:", err);
        toast.error("Failed to trigger manual sync.");
      })
      .finally(() => {
        setSyncing(false);
      });
  };

  const getStatusColor = (status) => {
    if (status === "connected") return "#2ecc71";
    if (status === "disconnected") return "#95a5a6";
    return "#e74c3c";
  };

  return (
    <div className="admin-dashboard-container" style={{ display: "flex", backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <DashboardSidebarMain />
      
      <div className="main-content-area" style={{ flexGrow: 1, padding: "30px 40px", marginLeft: "280px" }}>
        <HeaderDashMain />
        <ToastContainer />

        {/* Dashboard Title & Connection Status */}
        <div style={{
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "35px",
          background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
          padding: "20px 30px",
          borderRadius: "16px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)"
        }}>
          <div>
            <h2 style={{ margin: 0, fontWeight: 700, color: "#2c3e50", display: "flex", alignItems: "center", gap: "10px" }}>
              <Icon icon="logos:google-merchant-center" style={{ fontSize: "36px" }} />
              Google Merchant Center Integration
            </h2>
            <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: "14px" }}>
              Manage Content API catalog sync, settings, and diagnostics.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "white", padding: "10px 20px", borderRadius: "30px", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.03)" }}>
            <span style={{
              width: "12px", 
              height: "12px", 
              borderRadius: "50%", 
              backgroundColor: getStatusColor(data?.connection?.status),
              display: "inline-block",
              boxShadow: `0 0 10px ${getStatusColor(data?.connection?.status)}`,
              animation: data?.connection?.status === "connected" ? "pulse 2s infinite" : "none"
            }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50", textTransform: "capitalize" }}>
              {data?.connection?.status === "connected" ? "Connected" : data?.connection?.status === "disconnected" ? "Disconnected" : "Connection Error"}
            </span>
          </div>
        </div>

        {/* Sync Controls & Stats Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "25px", marginBottom: "35px" }}>
          
          {/* Card 1: Total Products */}
          <div style={{ background: "white", padding: "20px 25px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 600 }}>TOTAL ACTIVE PRODUCTS</span>
              <Icon icon="mdi:package-variant" style={{ fontSize: "24px", color: "#3498db" }} />
            </div>
            <h3 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#2c3e50" }}>{data?.stats?.total_products}</h3>
          </div>

          {/* Card 2: Synchronized */}
          <div style={{ background: "white", padding: "20px 25px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 600 }}>SYNCHRONIZED (GMC)</span>
              <Icon icon="mdi:check-circle-outline" style={{ fontSize: "24px", color: "#2ecc71" }} />
            </div>
            <h3 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#2c3e50" }}>{data?.stats?.synchronized_products}</h3>
          </div>

          {/* Card 3: Pending Sync */}
          <div style={{ background: "white", padding: "20px 25px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 600 }}>PENDING SYNC</span>
              <Icon icon="mdi:clock-alert-outline" style={{ fontSize: "24px", color: "#f39c12" }} />
            </div>
            <h3 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#2c3e50" }}>{data?.stats?.pending_products}</h3>
          </div>

          {/* Card 4: Failed Sync */}
          <div style={{ background: "white", padding: "20px 25px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 600 }}>FAILED SYNC</span>
              <Icon icon="mdi:alert-circle-outline" style={{ fontSize: "24px", color: "#e74c3c" }} />
            </div>
            <h3 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#2c3e50" }}>{data?.stats?.failed_products}</h3>
          </div>
        </div>

        {/* Sync Trigger Toolbar */}
        <div style={{
          display: "flex",
          gap: "15px",
          marginBottom: "35px",
          background: "white",
          padding: "20px 25px",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
          alignItems: "center"
        }}>
          <h4 style={{ margin: 0, color: "#2c3e50", fontSize: "15px", fontWeight: 600, marginRight: "auto" }}>
            <Icon icon="mdi:sync" style={{ verticalAlign: "middle", marginRight: "8px" }} />
            Sync Catalog manually:
          </h4>
          
          <button
            onClick={() => handleManualSync(false)}
            disabled={syncing || loading}
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              border: "none",
              background: "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
              color: "white",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 12px rgba(46, 204, 113, 0.2)",
              transition: "transform 0.2s"
            }}
          >
            <Icon icon="mdi:play" />
            Sync All Products
          </button>

          <button
            onClick={() => handleManualSync(true)}
            disabled={syncing || loading || data?.stats?.failed_products === 0}
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              border: "1px solid #e74c3c",
              background: "white",
              color: "#e74c3c",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s"
            }}
          >
            <Icon icon="mdi:refresh" />
            Retry Failed Synchronizations
          </button>
        </div>

        {/* Main Grid: Settings & Sync Details */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "35px", marginBottom: "35px" }}>
          
          {/* Settings Card */}
          <div style={{ background: "white", padding: "30px", borderRadius: "16px", boxShadow: "0 4px 25px rgba(0,0,0,0.03)" }}>
            <h3 style={{ margin: "0 0 25px", color: "#2c3e50", fontWeight: 700, display: "flex", alignItems: "center", gap: "10px" }}>
              <Icon icon="mdi:cog" style={{ color: "#0f7c49" }} />
              API Settings
            </h3>
            
            <form onSubmit={handleSaveSettings}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Merchant ID</label>
                <input
                  type="text"
                  value={settings.merchant_id}
                  onChange={(e) => setSettings({ ...settings, merchant_id: e.target.value })}
                  style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                  placeholder="e.g. 5340620857"
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Target Country</label>
                  <input
                    type="text"
                    value={settings.target_country}
                    onChange={(e) => setSettings({ ...settings, target_country: e.target.value })}
                    style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                    placeholder="IN"
                    maxLength={2}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Content Language</label>
                  <input
                    type="text"
                    value={settings.content_language}
                    onChange={(e) => setSettings({ ...settings, content_language: e.target.value })}
                    style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                    placeholder="en"
                    required
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Currency</label>
                  <input
                    type="text"
                    value={settings.currency}
                    onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                    style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                    placeholder="INR"
                    maxLength={3}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Default Shipping Cost (INR)</label>
                  <input
                    type="number"
                    value={settings.shipping_cost}
                    onChange={(e) => setSettings({ ...settings, shipping_cost: parseFloat(e.target.value) || 0 })}
                    style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                    min={0}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>Default Google Product Category</label>
                <input
                  type="text"
                  value={settings.default_category}
                  onChange={(e) => setSettings({ ...settings, default_category: e.target.value })}
                  style={{ width: "100%", padding: "12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", color: "#334155" }}
                  placeholder="e.g. Food, Beverages & Tobacco"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #0f7c49 0%, #187837 100%)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "15px",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(15, 124, 73, 0.25)",
                }}
              >
                Save GMC Settings
              </button>
            </form>
          </div>

          {/* Diagnostics Card */}
          <div style={{ background: "white", padding: "30px", borderRadius: "16px", boxShadow: "0 4px 25px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" }}>
            <h3 style={{ margin: "0 0 25px", color: "#2c3e50", fontWeight: 700, display: "flex", alignItems: "center", gap: "10px" }}>
              <Icon icon="mdi:alert-circle" style={{ color: "#e74c3c" }} />
              Diagnostics (Rejected Products)
            </h3>

            <div style={{ flexGrow: 1, overflowY: "auto", maxHeight: "400px" }}>
              {data?.diagnostics?.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                  <Icon icon="mdi:check-decagram-outline" style={{ fontSize: "48px", color: "#2ecc71" }} />
                  <p style={{ margin: "10px 0 0", fontSize: "14px" }}>No rejected products detected.</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                      <th style={{ padding: "10px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>PRODUCT</th>
                      <th style={{ padding: "10px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>SKU</th>
                      <th style={{ padding: "10px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>ERROR DIAGNOSIS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.diagnostics.map((log, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "10px", fontSize: "13px", fontWeight: 500, color: "#334155" }}>
                          {log?.product?.product_name || "Unknown Product"}
                        </td>
                        <td style={{ padding: "10px", fontSize: "13px", color: "#64748b" }}>
                          {log?.product?.product_code || "N/A"}
                        </td>
                        <td style={{ padding: "10px", fontSize: "12px", color: "#e74c3c", fontWeight: 500 }}>
                          {log?.error_message || "API rejection error"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Sync History Table */}
        <div style={{ background: "white", padding: "30px", borderRadius: "16px", boxShadow: "0 4px 25px rgba(0,0,0,0.03)" }}>
          <h3 style={{ margin: "0 0 25px", color: "#2c3e50", fontWeight: 700, display: "flex", alignItems: "center", gap: "10px" }}>
            <Icon icon="mdi:history" style={{ color: "#3498db" }} />
            Synchronization History
          </h3>

          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f1f5f9", textAlign: "left" }}>
                <th style={{ padding: "12px 15px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>TIME</th>
                <th style={{ padding: "12px 15px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>PRODUCT</th>
                <th style={{ padding: "12px 15px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>ACTION</th>
                <th style={{ padding: "12px 15px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>STATUS</th>
                <th style={{ padding: "12px 15px", color: "#64748b", fontSize: "12px", fontWeight: 600 }}>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {data?.history?.data?.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: "14px" }}>
                    No sync logs recorded yet. Run a manual sync to start.
                  </td>
                </tr>
              ) : (
                data?.history?.data?.map((log, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 15px", fontSize: "13px", color: "#64748b" }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "13px", fontWeight: 500, color: "#334155" }}>
                      {log?.product?.product_name || `ID: ${log.product_id}`}
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "13px", color: "#475569" }}>
                      <span style={{ textTransform: "capitalize", padding: "4px 8px", borderRadius: "4px", backgroundColor: "#f1f5f9", fontSize: "11px", fontWeight: 600 }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "13px" }}>
                      <span style={{
                        padding: "4px 8px", 
                        borderRadius: "12px", 
                        fontSize: "11px", 
                        fontWeight: 600,
                        color: log.status === "success" ? "#15803d" : "#b91c1c",
                        backgroundColor: log.status === "success" ? "#dcfce7" : "#fee2e2"
                      }}>
                        {log.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "12px", color: log.status === "success" ? "#64748b" : "#b91c1c" }}>
                      {log.status === "success" ? "Synced to GMC" : log.error_message}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {data?.history?.last_page > 1 && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              <button
                disabled={historyPage === 1}
                onClick={() => setHistoryPage(historyPage - 1)}
                style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "white", cursor: "pointer" }}
              >
                Previous
              </button>
              <span style={{ alignSelf: "center", fontSize: "14px", color: "#64748b" }}>
                Page {historyPage} of {data?.history?.last_page}
              </span>
              <button
                disabled={historyPage === data?.history?.last_page}
                onClick={() => setHistoryPage(historyPage + 1)}
                style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "white", cursor: "pointer" }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default GoogleMerchantMain;
